import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import { Stack, Typography } from '@mui/material';
import React from 'react';

import { OsTabs } from 'components/atoms/OsTabs';
import { OperatingSystem } from 'utils/osDetection';
import { CliInstructionStep, ItNote } from './cliInstructionTypes';
import { CliCommandBlock } from './CopyableCliBlock';
import InlineItNote from './InlineItNote';
import OsInstallInstructions from './OsInstallInstructions';
import { StepHelp } from './StepHelp';

interface BuildStepsParams {
  defaultOperatingSystem?: OperatingSystem;
  configureStep?: CliInstructionStep;
  profileStep?: CliInstructionStep;
  connectStepContent?: React.ReactNode;
  itNotes: Record<'install' | 'connect' | 'upload', ItNote | undefined>;
  onOpenSummary: () => void;
  selectedOs: OperatingSystem;
  setSelectedOs: (operatingSystem: OperatingSystem) => void;
  uploadStepContent: React.ReactNode;
  uploadStepTitle?: string;
  validateStep?: CliInstructionStep;
}

export const getTerminalOpenGuidance = (selectedOs: OperatingSystem) => {
  if (selectedOs === 'windows') {
    return 'On Windows, press Win + R, type wt or cmd, and press Enter to open Windows Terminal or Command Prompt.';
  }

  if (selectedOs === 'linux') {
    return 'On Linux, press Ctrl + Alt + T to open a terminal window in most desktop environments.';
  }

  return 'On macOS, press Command + Space, type Terminal, and press Enter.';
};

export const ItNoteForUploadStep = ({ itNote, onOpenSummary }: { itNote?: ItNote; onOpenSummary: () => void }) =>
  Boolean(itNote) && (
    <InlineItNote title={itNote!.title} body={itNote!.body} message={itNote!.message} onOpenSummary={onOpenSummary} />
  );

export const buildCliInstructionSteps = ({
  profileStep,
  configureStep,
  connectStepContent,
  itNotes,
  onOpenSummary,
  selectedOs,
  defaultOperatingSystem,
  setSelectedOs,
  uploadStepContent,
  uploadStepTitle,
  validateStep,
}: BuildStepsParams): CliInstructionStep[] => {
  const installSteps: CliInstructionStep[] = [
    {
      title: 'Initial setup',
      icon: <ComputerOutlinedIcon color="primary" fontSize="small" />,
      content: (
        <>
          <Typography variant="body2" color="text.secondary">
            Install AWS CLI, then continue. If it is already installed, continue.
          </Typography>
          <StepHelp
            items={[
              {
                question: 'What is AWS CLI?',
                answer: 'A program that runs the upload from a terminal. It is not the AWS website.',
              },
              {
                question: 'I cannot install software.',
                answer: 'Use the IT note for AWS CLI installation in this step, and send it to your IT team.',
              },
            ]}
          />
          <OsTabs
            selectedOs={selectedOs}
            defaultOperatingSystem={defaultOperatingSystem}
            setSelectedOs={setSelectedOs}
            ariaLabel="AWS CLI install instructions by operating system"
          />
          <OsInstallInstructions selectedOs={selectedOs} />
          <ItNoteForUploadStep itNote={itNotes?.install} onOpenSummary={onOpenSummary} />
        </>
      ),
    },
    {
      title: 'Check AWS CLI',
      icon: <CheckCircleOutlineIcon color="primary" fontSize="small" />,
      content: (
        <>
          <Typography variant="body2" color="text.secondary">
            Open a terminal, run this command, then continue. {getTerminalOpenGuidance(selectedOs)}
          </Typography>
          <StepHelp
            items={[
              {
                question: 'The terminal says command not found.',
                answer: 'Close the terminal, open a new one, and run the command again.',
              },
              {
                question: 'It printed something else.',
                answer: 'Return to the install step, or contact your IT team.',
              },
            ]}
          />
          <CliCommandBlock command="aws --version" />
          <Typography variant="body2" color="text.secondary">
            A version such as aws-cli/2.xx means it is installed.
          </Typography>
        </>
      ),
    },
  ];
  const connectStep: CliInstructionStep = {
    title: 'Connect your AWS CLI',
    icon: <SecurityOutlinedIcon color="primary" fontSize="small" />,
    content: (
      <>
        {connectStepContent}
        <ItNoteForUploadStep itNote={itNotes?.connect} onOpenSummary={onOpenSummary} />
      </>
    ),
  };

  const uploadStep: CliInstructionStep = {
    title: uploadStepTitle || 'Upload slides',
    icon: <TerminalOutlinedIcon color="primary" fontSize="small" />,
    content: <Stack spacing={1}>{uploadStepContent}</Stack>,
  };

  return [
    ...installSteps,
    ...(connectStepContent ? [connectStep] : []),
    ...(profileStep ? [profileStep] : []),
    ...(configureStep
      ? [
          {
            ...configureStep,
            content: (
              <Stack spacing={1.5}>
                {configureStep.content}
                <ItNoteForUploadStep itNote={itNotes?.connect} onOpenSummary={onOpenSummary} />
              </Stack>
            ),
          },
        ]
      : []),
    ...(validateStep
      ? [
          {
            ...validateStep,
            content: (
              <Stack spacing={1.5}>
                {validateStep.content}
                <ItNoteForUploadStep itNote={itNotes?.upload} onOpenSummary={onOpenSummary} />
              </Stack>
            ),
          },
        ]
      : []),
    uploadStep,
  ];
};
